import {
  MotifCard,
  MotifCardBody,
  MotifCardHeader,
  MotifTruncate,
  MotifIcon,
} from "@ey-xd/motif-react";
import "./OrderCard.scss";
import { useEffect, useState } from "react";
import { ViewDetails } from "../ViewDetails/ViewDetails";
import {
  calculatePeriodTotalCost,
  calculateTotalCost,
  getCostIntervalForCostMetric,
  getCostTypeLabel,
  getDuration,
  getDurationType,
  getRelationDuration,
  getUnitsUsedForPeriod,
} from "../../CheckoutHelper";
import {
  ConfigResponseDetails,
  SkuDetailsType,
  ViewDetailType,
} from "../../type";
import {
  IconoirEditPencil,
  MotifActionIcDelete24px,
  IconoirArrowRight,
  IconoirWarningCircle,
} from "@ey-xd/motif-icon";
import { ResponseDetails } from "../../../../store/slices/CheckoutSlice";
import UpdateOrderModal from "../UpdateOrderModal/UpdateOrderModal";
import DeleteProductModal from "../DeleteProductModal/DeleteProductModal";
import NumericInput from "../../../../components/NumericInput/NumericInput";
import { MAX_QUANTITY } from "../../../../../src/Constant";
import { validateCostInterval } from "../../../../utils/Validator";
import InfoTooltip from "../../../../components/InfoTooltip";
import {
  numberOfPeriodsInfo,
  skuIDInfo,
  subTotalInfo,
  unitCostInfo,
  unitPerPeriodInfo,
} from "../../../../utils/skuTable";

interface OrderCardProps {
  productName: string;
  productId: string; // Optional, if needed for further processing
  assetAdvice?: null | JSX.Element | undefined | string;
  skuDetails: SkuDetailsType[]; // Assuming skuDetails is of type OrderBundleType
  checkOutListState?: ResponseDetails | null;
  setCheckOutListState: React.Dispatch<
    React.SetStateAction<ResponseDetails | null>
  >;
  setReloadPage: React.Dispatch<React.SetStateAction<boolean>>;
  isChangedActive: boolean;
  setIsChangedActive: React.Dispatch<React.SetStateAction<boolean>>;
  setDisableBtns: React.Dispatch<React.SetStateAction<boolean>>;
  disableBtns: boolean; // Optional, if needed for further processing
  setShowEngagementException: React.Dispatch<React.SetStateAction<boolean>>;
  retiredSkuList: string[];
  onOrderUpdated: () => void;
  viewMode: boolean;
  configData: ConfigResponseDetails | null;
}
export const OrderCard = ({
  productName,
  productId,
  assetAdvice,
  skuDetails,
  checkOutListState,
  setCheckOutListState,
  setReloadPage,
  isChangedActive,
  setIsChangedActive,
  setDisableBtns,
  disableBtns,
  setShowEngagementException,
  retiredSkuList = [],
  onOrderUpdated,
  viewMode = false,
  configData,
}: OrderCardProps) => {
  const [skuListViewData, setSkuListViewData] = useState(skuDetails);
  const [isViewDetailsOpen, setIsViewDetailsOpen] = useState(false);
  const [viewDetailsData, setViewDetailsData] = useState<ViewDetailType>({
    isViewDetailsOpen: isViewDetailsOpen,
    skuId: "",
    skuName: "",
    skuDescription: "",
    skuCommercialStatus: "",
    skuCost: 0,
    returnable: false,
    skuCostInterval: "",
    setIsViewDetailsOpen: setIsViewDetailsOpen,
    assetAdvice: assetAdvice || null,
    skuCostMetricCd: "",
    skuCostMacroCategoryCd: "",
    skuCostMetricNSR: 0,
    skuIndexation: 0,
    skuStatusName: "",
  });
  const [showSkuModal, setShowSkuModal] = useState<boolean>(false);
  const [showSkuDeleteModal, setShowSkuDeleteModal] = useState<boolean>(false);
  const handleViewDetails = (skuDetails: SkuDetailsType) => {
    setIsViewDetailsOpen(true);
    if (!viewDetailsData || viewDetailsData === null) {
      return;
    }
    setViewDetailsData({
      ...viewDetailsData,
      isViewDetailsOpen: true,
      skuId: skuDetails.SkuId,
      skuName: skuDetails.Name,
      skuDescription: skuDetails.Description,
      skuCommercialStatus: skuDetails.StatusCd,
      skuCost: skuDetails.CostMetricCost,
      returnable: skuDetails.IsReturnable,
      skuCostInterval: skuDetails.CostIntervalCd,
      skuCostMetricCd: skuDetails.CostMetricCd,
      skuCostMacroCategoryCd: skuDetails.CostMacroCategoryCd,
      skuCostMetricNSR: skuDetails.CostMetricNSR,
      skuIndexation: skuDetails.Indexation,
      skuStatusName: skuDetails.StatusName ? skuDetails.StatusName : "",
    });
  };

  const isFixedCostSku = (sku: any) => {
    return sku.CostMetricCd === "fix_cost" || sku.CostIntervalCd === "onetime";
  };

  useEffect(() => {
    if (skuListViewData && skuListViewData !== null) {
      const isHavingDurationError: boolean[] = [];
      setSkuListViewData(
        skuListViewData.map((sku: any) => {
          const isError = !validateCostInterval(
            sku.CostIntervalCd,
            sku.OrderInterval,
            sku.CostIntervalMin,
          );
          isHavingDurationError.push(isError);
          return {
            ...sku,
            OrderQuantity:
              sku?.CostIntervalCd == "onetime" ? 1 : sku?.OrderQuantity,
            Quantity: sku?.CostIntervalCd == "onetime" ? 1 : sku?.OrderQuantity,
            QuantityError:
              sku?.CostIntervalCd == "onetime" ? "" : sku?.QuantityError || "",
            Duration: sku?.CostIntervalCd == "onetime" ? 1 : sku?.OrderInterval,
            DurationError:
              sku?.CostIntervalCd == "onetime" ? "" : sku?.DurationError || "",
            SkuRelation: sku?.SkuRelation.map((relation: any) => {
              const isError = !validateCostInterval(
                relation.CostIntervalCd,
                relation.OrderInterval,
                relation.CostIntervalMin,
              );
              isHavingDurationError.push(isError);
              return {
                ...relation,
                OrderQuantity:
                  relation.CostIntervalCd == "onetime"
                    ? 1
                    : relation.OrderQuantity,
                Quantity:
                  relation.CostIntervalCd == "onetime"
                    ? 1
                    : relation.OrderQuantity,
                QuantityError:
                  relation.CostIntervalCd == "onetime"
                    ? ""
                    : relation.QuantityError,
                Duration:
                  relation.CostIntervalCd == "onetime"
                    ? 1
                    : relation.OrderInterval,
                DurationError:
                  relation.CostIntervalCd == "onetime"
                    ? ""
                    : relation.DurationError || "",
              };
            }),
          };
        }),
      );
      // check any one SKU has duration error
      if (isHavingDurationError.includes(true)) {
        setDisableBtns(true);
      }
    }
  }, []);

  const handleQuantityChange = (
    skuId: string,
    quantity: string,
    skuListViewData: SkuDetailsType[],
    setSkuListViewData: (value: any) => void,
    parentSkuId: string | null = null,
    setIsChangedActive: React.Dispatch<React.SetStateAction<boolean>>,
  ) => {
    let updatedSkuListViewData = skuListViewData;
    if (parentSkuId !== null && parentSkuId !== undefined) {
      updatedSkuListViewData = skuListViewData.map((sku: SkuDetailsType) => {
        if (sku?.SkuId === parentSkuId) {
          const updatedSkuRelation = sku?.SkuRelation.map((relation: any) => {
            if (relation.SkuId === skuId) {
              return {
                ...relation,
                OrderQuantity: Number(quantity),
                Quantity: Number(quantity),
                QuantityError:
                  quantity === ""
                    ? "Required"
                    : Number(quantity) <= 0
                      ? "Min 1"
                      : Number(quantity) > MAX_QUANTITY
                        ? `Max value is ${MAX_QUANTITY}`
                        : "",
              };
            }
            return relation;
          });
          return { ...sku, SkuRelation: updatedSkuRelation };
        }
        return sku;
      });
      setSkuListViewData(updatedSkuListViewData);
      setCheckOutListState((prevState: any) => {
        if (!prevState) return null;
        return {
          ...prevState,
          Details: prevState.Details.map((detail: any) => {
            return {
              ...detail,
              SkuDetails: detail.SkuDetails.map((sku: any) => {
                return {
                  ...sku,
                  SkuRelation: sku?.SkuRelation.map((skuRelation: any) => {
                    if (
                      skuRelation.SkuId === skuId &&
                      sku?.SkuId === parentSkuId
                    ) {
                      return {
                        ...skuRelation,
                        OrderQuantity: Number(quantity),
                        Quantity: Number(quantity),
                        QuantityError:
                          quantity === ""
                            ? "Required"
                            : Number(quantity) <= 0
                              ? "Min 1"
                              : Number(quantity) > MAX_QUANTITY
                                ? `Max value is ${MAX_QUANTITY}`
                                : "",
                      };
                    }
                    return skuRelation;
                  }),
                };
              }),
            };
          }),
        };
      });
      setIsChangedActive(true);
      return;
    } else {
      updatedSkuListViewData = skuListViewData.map((sku: SkuDetailsType) => {
        if (sku?.SkuId === skuId) {
          return {
            ...sku,
            OrderQuantity: Number(quantity),
            Quantity: Number(quantity),
            QuantityError:
              quantity === ""
                ? "Required"
                : Number(quantity) <= 0
                  ? "Min 1"
                  : Number(quantity) > MAX_QUANTITY
                    ? `Max value is ${MAX_QUANTITY}`
                    : "",
          };
        }
        return sku;
      });
      setSkuListViewData(updatedSkuListViewData);
      const updateCheckListData = { ...checkOutListState };
      const updateCheckListDataNew = updateCheckListData?.Details?.map(
        (detail: any) => {
          if (detail.SkuDetails.some((sku: any) => sku?.SkuId === skuId)) {
            return {
              ...detail,
              SkuDetails: detail.SkuDetails.map((sku: any) => {
                if (sku?.SkuId === skuId) {
                  return {
                    ...sku,
                    OrderQuantity: Number(quantity),
                    Quantity: Number(quantity),
                    QuantityError:
                      quantity === ""
                        ? "Required"
                        : Number(quantity) <= 0
                          ? "Min 1"
                          : Number(quantity) > MAX_QUANTITY
                            ? `Max value is ${MAX_QUANTITY}`
                            : "",
                  };
                }
                return sku;
              }),
            };
          } else {
            return detail;
          }
        },
      );
      setCheckOutListState((prevState: any) => {
        if (!prevState) return null;
        return {
          ...prevState,
          Details: updateCheckListDataNew,
        };
      });
      setIsChangedActive(true);
    }
    const isError = updatedSkuListViewData.some(
      (sku: SkuDetailsType) =>
        sku?.DurationError !== "" || sku?.QuantityError !== "",
    );
    if (isError) {
      setDisableBtns(true);
    } else {
      setDisableBtns(false);
    }
  };

  const editBtnHandler = () => {
    if (!disableBtns) setShowSkuModal(true);
  };

  const updateSkuModalSaveHandler = () => {
    setReloadPage(true);
    setShowSkuModal(false);
  };

  return (
    <>
      <div className="order-card">
        <MotifCard className="motif-card-default">
          <MotifCardHeader>
            <div className="motif-card-subheader">
              <div className="motif-h6-default-regular">{productName}</div>
            </div>
            <div className="table-action-grid">
              {/* <div className="saved-for-later">Save for Later</div> */}
              <div
                className={`action-btn ${disableBtns ? "no-cursor" : ""}`}
                onClick={editBtnHandler}
              >
                <MotifIcon iconFunction={IconoirEditPencil} size="20" />
              </div>
              <div
                className={`action-btn ${disableBtns ? "no-cursor" : ""}`}
                onClick={() =>
                  !disableBtns ? setShowSkuDeleteModal(true) : {}
                }
              >
                <MotifIcon iconFunction={MotifActionIcDelete24px} size="20" />
              </div>
            </div>
          </MotifCardHeader>
          <MotifCardBody>
            <table className="motif-table-standard">
              <thead>
                <tr>
                  <th className="sku-th">
                    <div className="label-with-info">
                      <div>SKU name/SKU ID</div>
                      <div className="info-icon">
                        <InfoTooltip
                          description={skuIDInfo()}
                          id="sku-id-info"
                        />
                      </div>
                    </div>
                  </th>
                  <th>
                    <div className="label-with-info right-align-grid">
                      <div>Unit Cost</div>
                      <div className="info-icon">
                        <InfoTooltip
                          description={unitCostInfo()}
                          id="unit-cost-info"
                        />
                      </div>
                    </div>
                  </th>
                  <th className="qty-td">
                    <div className="label-with-info">
                      <div>Units per period</div>
                      <div className="info-icon">
                        <InfoTooltip
                          description={unitPerPeriodInfo()}
                          id="units-per-period-info"
                        />
                      </div>
                    </div>
                  </th>
                  <th className="duration-td">
                    <div className="label-with-info">
                      <div>Number of periods</div>
                      <div className="info-icon">
                        <InfoTooltip
                          description={numberOfPeriodsInfo()}
                          id="number-of-periods-info"
                        />
                      </div>
                    </div>
                  </th>
                  <th>
                    <div className="label-with-info right-align-grid">
                      <div>Subtotal</div>
                      <div className="info-icon">
                        <InfoTooltip
                          description={subTotalInfo()}
                          id="subtotal-info"
                        />
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {skuListViewData.map((sku: SkuDetailsType, index: number) => {
                  let skuError = retiredSkuList.includes(sku?.SkuId)
                    ? true
                    : false;
                  if (sku?.SkuRelation?.length === 0) {
                    return (
                      <tr key={index}>
                        <td
                          className={`sku-cell ${skuError ? "sku-error" : ""}`}
                        >
                          <div className="sku-title title">
                            {skuError && (
                              <MotifIcon
                                src={IconoirWarningCircle}
                                className="warning-icon"
                              />
                            )}
                            <MotifTruncate maxLines={1} maxWidth={160}>
                              {sku?.Name}
                            </MotifTruncate>
                          </div>
                          <div className="sku-number suggested-table-content">
                            #
                            <MotifTruncate
                              maxLines={1}
                              maxWidth={100}
                              tooltipProps={{ placement: "top" }}
                            >
                              {sku?.SkuId}
                            </MotifTruncate>
                            {getCostTypeLabel(sku?.CostMetricCd, configData)}
                          </div>
                          <div
                            onClick={() => handleViewDetails(sku)}
                            className="view-details-link"
                          >
                            <div>View Details</div>
                            <div className="icon-grid">
                              {<MotifIcon iconFunction={IconoirArrowRight} />}
                            </div>
                          </div>
                        </td>
                        <td className="unit-cost-td single-sku">
                          <div className="right-align-field">
                            {sku?.CostMetricCost.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                              maximumFractionDigits: 2,
                              minimumFractionDigits: 2,
                            })}
                          </div>
                          <div className="suggested-table-content right-align-field-flex">
                            {getCostIntervalForCostMetric(
                              sku.CostIntervalCd,
                              sku.CostMetricCd,
                            )}
                          </div>
                        </td>
                        <td className="qty-td single-sku">
                          <div className="left-align-field-flex">
                            {isFixedCostSku(sku) ? (
                              <span>01</span>
                            ) : (
                              <>
                                <NumericInput
                                  id={sku?.SkuId}
                                  value={sku?.OrderQuantity?.toString() ?? ""}
                                  error={sku?.QuantityError}
                                  min={0}
                                  max={MAX_QUANTITY}
                                  placeholder="Qty"
                                  disabled={viewMode}
                                  onChange={(val) =>
                                    handleQuantityChange(
                                      sku?.SkuId,
                                      val,
                                      skuListViewData,
                                      setSkuListViewData,
                                      null,
                                      setIsChangedActive,
                                    )
                                  }
                                  label="Quantity"
                                />
                              </>
                            )}
                          </div>
                          <div className="suggested-table-content">
                            {getUnitsUsedForPeriod(
                              sku.CostIntervalCd,
                              sku.CostMetricCd,
                              sku.OrderQuantity,
                            )}
                          </div>
                        </td>
                        <td className="duration-td">
                          <div className="left-align-field-flex">
                            {getDuration(
                              sku,
                              skuListViewData,
                              setSkuListViewData,
                              null,
                              setCheckOutListState,
                              checkOutListState,
                              setIsChangedActive,
                              setDisableBtns,
                              viewMode,
                            )}
                          </div>
                          <div className="suggested-table-content">
                            {getDurationType(sku?.CostIntervalCd)}
                          </div>
                        </td>
                        <td className="subtotal-td single-sku">
                          <div>
                            <div className={"order-total right-align-field"}>
                              {calculateTotalCost(sku)}{" "}
                            </div>
                            <div className="suggested-table-content right-align-field-flex">
                              {calculatePeriodTotalCost(sku)} /
                              {getDurationType(sku?.CostIntervalCd)
                                ? getDurationType(sku?.CostIntervalCd)
                                : ""}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  } else {
                    return (
                      <tr key={index}>
                        <td
                          className={`sku-cell ${skuError ? "sku-error" : ""}`}
                        >
                          <ul className="tree">
                            <li className="tree-trigger">
                              <div className="table-cell">
                                <div>
                                  {skuError && (
                                    <MotifIcon
                                      src={IconoirWarningCircle}
                                      className="warning-icon"
                                    />
                                  )}
                                  <span className="sku-title title">
                                    <MotifTruncate maxLines={1} maxWidth={160}>
                                      {sku?.Name}
                                    </MotifTruncate>
                                  </span>
                                </div>
                                <div className="sku-number suggested-table-content">
                                  #
                                  <MotifTruncate
                                    maxLines={1}
                                    maxWidth={100}
                                    tooltipProps={{ placement: "top" }}
                                  >
                                    {sku?.SkuId}
                                  </MotifTruncate>
                                  {getCostTypeLabel(
                                    sku?.CostMetricCd,
                                    configData,
                                  )}
                                </div>
                                <div
                                  onClick={() => handleViewDetails(sku)}
                                  className="view-details-link"
                                >
                                  <div>View Details</div>
                                  <div className="icon-grid">
                                    {
                                      <MotifIcon
                                        iconFunction={IconoirArrowRight}
                                      />
                                    }
                                  </div>
                                </div>
                              </div>
                            </li>
                            <ul>
                              {sku?.SkuRelation?.map((skuRelation: any) => {
                                let skuError = retiredSkuList.includes(
                                  skuRelation?.SkuId,
                                )
                                  ? true
                                  : false;
                                return (
                                  <li>
                                    <div className="table-cell">
                                      <div className="sku-title title">
                                        {skuError && (
                                          <MotifIcon
                                            src={IconoirWarningCircle}
                                            className="warning-icon"
                                          />
                                        )}
                                        <MotifTruncate
                                          maxLines={1}
                                          maxWidth={160}
                                        >
                                          {skuRelation.Name}
                                        </MotifTruncate>
                                      </div>
                                      <div className="sku-number">
                                        <div className="sku-number suggested-table-content">
                                          #
                                          <MotifTruncate
                                            maxLines={1}
                                            maxWidth={80}
                                            tooltipProps={{ placement: "top" }}
                                          >
                                            {skuRelation.SkuId}
                                          </MotifTruncate>
                                          {getCostTypeLabel(
                                            skuRelation?.CostMetricCd,
                                            configData,
                                          )}
                                        </div>
                                        <span
                                          onClick={() =>
                                            handleViewDetails(skuRelation)
                                          }
                                          className="view-details-link"
                                        >
                                          <div>View Details</div>
                                          <div className="icon-grid">
                                            {
                                              <MotifIcon
                                                iconFunction={IconoirArrowRight}
                                              />
                                            }
                                          </div>
                                        </span>
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </ul>
                        </td>
                        <td className="unit-cost-td relation-col">
                          <div className="table-cell-child table-cell unit-cost-relation right-align-field">
                            {sku?.CostMetricCost.toLocaleString("en-US", {
                              style: "currency",
                              currency: "USD",
                              maximumFractionDigits: 2,
                              minimumFractionDigits: 2,
                            })}
                            <div className="suggested-table-content right-align-field-flex">
                              {getCostIntervalForCostMetric(
                                sku.CostIntervalCd,
                                sku.CostMetricCd,
                              )}
                            </div>
                          </div>
                          {sku?.SkuRelation?.map((skuRelation: any) => {
                            return (
                              <div
                                key={skuRelation.SkuId}
                                className="table-cell-child table-cell unit-cost-relation right-align-field"
                              >
                                {skuRelation.CostMetricCost.toLocaleString(
                                  "en-US",
                                  {
                                    style: "currency",
                                    currency: "USD",
                                    maximumFractionDigits: 2,
                                    minimumFractionDigits: 2,
                                  },
                                )}
                                <div className="suggested-table-content right-align-field-flex">
                                  {getCostIntervalForCostMetric(
                                    skuRelation.CostIntervalCd,
                                    skuRelation.CostMetricCd,
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </td>
                        <td className="qty-td relation-col">
                          <div className="table-cell-child table-cell relation-qty">
                            {isFixedCostSku(sku) ? (
                              <>
                                <div className="fix-qnt">01 </div>
                                <div className="suggested-table-content">
                                  {getUnitsUsedForPeriod(
                                    sku.CostIntervalCd,
                                    sku.CostMetricCd,
                                    sku.OrderQuantity,
                                  )}
                                </div>
                              </>
                            ) : (
                              <div className="table-cell-qty">
                                <NumericInput
                                  id={sku?.SkuId}
                                  value={sku?.OrderQuantity}
                                  error={sku?.QuantityError}
                                  min={0}
                                  max={MAX_QUANTITY}
                                  placeholder="Qty"
                                  disabled={viewMode}
                                  onChange={(val) =>
                                    handleQuantityChange(
                                      sku?.SkuId,
                                      val,
                                      skuListViewData,
                                      setSkuListViewData,
                                      null,
                                      setIsChangedActive,
                                    )
                                  }
                                  label="Quantity"
                                />
                                <div className="suggested-table-content">
                                  {getUnitsUsedForPeriod(
                                    sku.CostIntervalCd,
                                    sku.CostMetricCd,
                                    sku.OrderQuantity,
                                  )}
                                </div>
                              </div>
                            )}
                          </div>
                          {sku?.SkuRelation?.map((skuRelation: any) => {
                            return (
                              <div
                                className="table-cell-child table-cell relation-qty quantity-relation fix-quantity-relation"
                                key={skuRelation.SkuId}
                              >
                                {isFixedCostSku(skuRelation) ? (
                                  <div className="fix-qnt-child">01</div>
                                ) : (
                                  <>
                                    <NumericInput
                                      id={skuRelation.SkuId}
                                      value={skuRelation.OrderQuantity}
                                      error={skuRelation.QuantityError}
                                      min={0}
                                      max={MAX_QUANTITY}
                                      placeholder="Qty"
                                      disabled={viewMode}
                                      onChange={(val) =>
                                        handleQuantityChange(
                                          skuRelation.SkuId,
                                          val,
                                          skuListViewData,
                                          setSkuListViewData,
                                          sku?.SkuId,
                                          setIsChangedActive,
                                        )
                                      }
                                      label="Quantity"
                                    />
                                    <div className="suggested-table-content">
                                      {getUnitsUsedForPeriod(
                                        skuRelation.CostIntervalCd,
                                        skuRelation.CostMetricCd,
                                        skuRelation.OrderQuantity,
                                      )}
                                    </div>
                                  </>
                                )}
                              </div>
                            );
                          })}
                        </td>
                        <td className="duration-td relation-col">
                          <div className="suggested-table-content table-cell-child table-cell relation-qty support-text">
                            {getDuration(
                              sku,
                              skuListViewData,
                              setSkuListViewData,
                              null,
                              setCheckOutListState,
                              checkOutListState,
                              setIsChangedActive,
                              setDisableBtns,
                              viewMode,
                            )}
                            <div className="suggested-table-content">
                              {getDurationType(sku?.CostIntervalCd)}
                            </div>
                          </div>
                          {sku?.SkuRelation?.map((skuRelation: any) => {
                            return (
                              <div
                                className="relation-duration table-cell-child table-cell relation-qty suggested-table-content support-text"
                                key={skuRelation.SkuId}
                              >
                                {getRelationDuration(
                                  skuRelation,
                                  skuListViewData,
                                  setSkuListViewData,
                                  sku?.SkuId,
                                  setCheckOutListState,
                                  checkOutListState,
                                  setIsChangedActive,
                                  setDisableBtns,
                                  viewMode,
                                )}
                                <div className="suggested-table-content">
                                  {getDurationType(skuRelation?.CostIntervalCd)}
                                </div>
                              </div>
                            );
                          })}
                        </td>
                        <td className="subtotal-td relation-col">
                          <div
                            className={`relation-total table-cell-child table-cell unit-cost-relation`}
                          >
                            <div>
                              <div className={"order-total right-align-field"}>
                                {calculateTotalCost(sku)}{" "}
                              </div>
                              <div className="suggested-table-content">
                                {calculatePeriodTotalCost(sku)} /
                                {getDurationType(sku?.CostIntervalCd)
                                  ? getDurationType(sku?.CostIntervalCd)
                                  : ""}
                              </div>
                            </div>
                          </div>
                          {sku?.SkuRelation?.map((skuRelation: any) => {
                            const total_with_padding =
                              skuRelation.CostIntervalCd == "onetime"
                                ? "no-padding"
                                : "total-width-padding";
                            return (
                              <>
                                <div
                                  className={`relation-total table-cell-child table-cell unit-cost-relation ${total_with_padding}`}
                                >
                                  <div>
                                    <div
                                      className={
                                        "order-total right-align-field"
                                      }
                                    >
                                      {calculateTotalCost(skuRelation)}{" "}
                                    </div>
                                    <div className="suggested-table-content">
                                      {calculatePeriodTotalCost(skuRelation)} /
                                      {getDurationType(
                                        skuRelation?.CostIntervalCd,
                                      )
                                        ? getDurationType(
                                            skuRelation?.CostIntervalCd,
                                          )
                                        : ""}
                                    </div>
                                  </div>
                                </div>
                              </>
                            );
                          })}
                        </td>
                      </tr>
                    );
                  }
                })}
              </tbody>
            </table>
          </MotifCardBody>
        </MotifCard>
      </div>
      {isViewDetailsOpen && <ViewDetails viewDetailsData={viewDetailsData} />}
      {showSkuModal && (
        <UpdateOrderModal
          show={showSkuModal}
          setShow={setShowSkuModal}
          onOrderUpdated={onOrderUpdated}
          onSave={updateSkuModalSaveHandler}
          productId={productId}
          productName={productName}
          checkOutListState={checkOutListState}
          setCheckOutListState={setCheckOutListState}
          assetAdvice={assetAdvice || null}
          isChangedActive={isChangedActive}
          setIsChangedActive={setIsChangedActive}
          setShowEngagementException={setShowEngagementException}
          retiredSkuList={retiredSkuList}
        />
      )}
      <DeleteProductModal
        isVisible={showSkuDeleteModal}
        name="deleteProductModel"
        popupCloseHandler={() => {
          setShowSkuDeleteModal(false);
        }}
        setReloadPage={setReloadPage}
        productId={productId}
        orderId={"" + checkOutListState?.OrderId || ""}
        productsCount={checkOutListState?.Details?.length || 0}
      />
    </>
  );
};
